import { states, useToast } from '@airship-platform/airship-patterns';
import { FlexBox, Loader } from '@airship-platform/jam-web/components';
import { PlusIcon } from '@airship-platform/jam-web/icons';
import { PhoneValidationSchema } from '@airship-platform/common/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import useOrganization from '../../../data-access/hooks/useOrganizations';
import { Organization } from '../../../data-access/types/models';
import { formatPhone } from '../../../data-access/utils/phone.utils';
import ROUTE_PATHS from '../../../routes/paths';
import OnboardingWrapper from './OnboardingWrapper';

const schema = z.object({
  organizationName: z.string().nonempty('Company Name is required'),
  organizationAddress: z.object({
    address1: z.string().min(1, 'Address is required'),
    address2: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zip: z.string().min(4, 'ZIP is required').max(5),
  }),
  organizationPhone: PhoneValidationSchema,
  numberOfEmployees: z.string().min(1, 'Number of Employees is required'),
});

type FormInputs = z.infer<typeof schema>;
type CompanyDetailsProps = {
  organizationId: string;
};

const CompanyDetails: React.FC<CompanyDetailsProps> = ({ organizationId }) => {
  const navigate = useNavigate();

  const {
    updateOrganization,
    uploadOrganizationLogo,
    isLoading,
    isFetching,
    fetchOrganization,
    organization,
  } = useOrganization();
  const { showToast } = useToast();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (organizationId) {
      fetchOrganization(organizationId);
    }
  }, [fetchOrganization, organizationId]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    values: {
      organizationName: organization?.organizationName || '',
      organizationAddress: {
        address1: organization?.organizationAddress?.address1 || '',
        address2: organization?.organizationAddress?.address2 || '',
        city: organization?.organizationAddress?.city || '',
        state: organization?.organizationAddress?.state || '',
        zip: organization?.organizationAddress?.zip || '',
      },
      organizationPhone: organization?.organizationPhone || '',
      numberOfEmployees: organization?.numberOfEmployees || '',
    },
    mode: 'onSubmit',
    resolver: zodResolver(schema),
  });

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (
        (file.type === 'image/jpeg' ||
          file.type === 'image/png' ||
          file.type === 'image/gif') &&
        file.size <= 10 * 1024 * 1024
      ) {
        setSelectedFile(file);
        setPreview(URL.createObjectURL(file));
      } else if (file.size > 10 * 1024 * 1024) {
        showToast('File size must be less than 10 MB.', 'warning');
      } else {
        showToast('Please upload a JPEG, PNG, or GIF file.', 'warning');
      }
    }
  };

  const handleFileDelete = () => {
    setSelectedFile(null);
    setPreview(null);
  };

  const handleSaveAndExit = async (data: Partial<Organization>) => {
    if (!organizationId) {
      return;
    }

    if (selectedFile) {
      await uploadOrganizationLogo(organizationId, selectedFile).then(
        (response) => {
          if (response.success === false) return;
        }
      );
    }

    await updateOrganization(organizationId, data).then((response) => {
      if (response.success === false) return;
      navigate(ROUTE_PATHS.HOME);
    });
  };

  const handleNextStep = async (data: Partial<Organization>) => {
    if (!organizationId) {
      return;
    }
    if (selectedFile) {
      await uploadOrganizationLogo(organizationId, selectedFile).then(
        (response) => {
          if (response.success === false) return;
        }
      );
    }
    await updateOrganization(organizationId, data).then((response) => {
      if (response.success === false) return;
      navigate(ROUTE_PATHS.onboardingStep('personal-details'));
    });
  };

  return (
    <OnboardingWrapper
      onSaveClick={{ onClick: handleSubmit(handleSaveAndExit) }}
      onNextClick={{
        onClick: handleSubmit(handleNextStep),
        isLoading: isLoading,
      }}
      buttonLabel={'Save & exit setup'}
    >
      <FlexBox
        flexDirection={'column'}
        alignItems={'center'}
        justifyContent={'center'}
        gap={4}
      >
        <Typography variant={'h2'}>Let's start with the basics</Typography>
        <FlexBox
          component={'form'}
          width={'100%'}
          display={'flex'}
          flexDirection={'column'}
          gap={2}
        >
          <Controller
            name={'organizationName'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                id={'organizationName'}
                error={!!errors.organizationName}
                label={'Company Name'}
                helperText={errors.organizationName?.message}
                fullWidth
                slotProps={{
                  htmlInput: { 'data-testid': 'organizationName' },
                }}
              />
            )}
          />
          <Controller
            name={'organizationAddress.address1'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={'Company address'}
                error={!!errors.organizationAddress?.address1}
                helperText={errors.organizationAddress?.address1?.message}
                fullWidth
              />
            )}
          />
          <Controller
            name={'organizationAddress.address2'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={'Address 2'}
                error={!!errors.organizationAddress?.address2}
                helperText={errors.organizationAddress?.address2?.message}
                fullWidth
              />
            )}
          />
          <FlexBox alignItems={'start'} gap={1}>
            <Controller
              name={'organizationAddress.city'}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={'City'}
                  error={!!errors.organizationAddress?.city}
                  helperText={errors.organizationAddress?.city?.message}
                  fullWidth
                />
              )}
            />
            <Controller
              name={'organizationAddress.state'}
              control={control}
              render={({ field }) => (
                <TextField
                  select={true}
                  {...field}
                  label={'State'}
                  error={!!errors.organizationAddress?.state}
                  helperText={errors.organizationAddress?.state?.message}
                >
                  {states.map((state) => (
                    <MenuItem key={state.code} value={state.name}>
                      {state.code}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name={'organizationAddress.zip'}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={'ZIP'}
                  error={!!errors.organizationAddress?.zip}
                  helperText={errors.organizationAddress?.zip?.message}
                  fullWidth
                />
              )}
            />
          </FlexBox>
          <Controller
            name={'organizationPhone'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                type={'tel'}
                label={'Phone number'}
                error={!!errors.organizationPhone}
                helperText={errors.organizationPhone?.message}
                fullWidth
                slotProps={{
                  htmlInput: {
                    'data-testid': 'phone',
                    onInput: formatPhone,
                  },
                }}
              />
            )}
          />
          <Controller
            name={'numberOfEmployees'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={'Number of Employees'}
                error={!!errors.numberOfEmployees}
                helperText={errors.numberOfEmployees?.message}
                fullWidth
              />
            )}
          />
        </FlexBox>
      </FlexBox>
      <FlexBox flexDirection={'column'} alignSelf={'flex-start'} gap={2}>
        <input
          accept={'image/jpeg,image/png,image/gif'}
          style={{ display: 'none' }}
          id={'file-upload'}
          ref={fileInputRef}
          type={'file'}
          onChange={handleFileChange}
        />
        {!preview && (
          <>
            {' '}
            <Typography variant="body3">Company Logo (optional)</Typography>
            <label htmlFor="file-upload">
              <Button
                variant={'outlined'}
                color={'gray'}
                size={'medium'}
                onClick={handleButtonClick}
                startIcon={<PlusIcon />}
              >
                Add Image
              </Button>
            </label>
          </>
        )}
        {preview && (
          <FlexBox flexDirection={'column'} alignItems={'flex-start'} gap={1}>
            <img
              src={preview}
              alt={'Preview'}
              style={{ maxWidth: '6.25rem', maxHeight: '3.5rem' }}
            />
            <FlexBox gap={1}>
              <Link color={'secondary'} onClick={handleButtonClick}>
                Replace
              </Link>
              <Link color={'secondary'} onClick={handleFileDelete}>
                Remove
              </Link>
            </FlexBox>
          </FlexBox>
        )}
      </FlexBox>
      <Loader isLoading={isFetching} />
    </OnboardingWrapper>
  );
};

export default CompanyDetails;
